import { useMemo } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { attributeDocuments } from "../lib/attribution";
import { ApiError } from "./client";
import * as admin from "./admin";
import * as moodle from "./moodle";
import * as rag from "./rag";
import * as sim from "./simPatient";
import { attachTask, cancelJob, finishJobs, startJob, useJob, type JobKind } from "./jobs";
import type { AnyRun, ApprovalStatus, CaseSection, CaseTemplate, ExtractionRun, MoodleCourse, MoodleDocument, ScenarioInput, ScenarioStatus, SyncEvent, TranscriptionStatus } from "./types";

export const keys = {
  courses: ["courses"] as const,
  coursePage: (offset: number, limit: number, includeDeleted: boolean) => ["courses", "page", offset, limit, includeDeleted] as const,
  course: (id: number) => ["courses", id] as const,
  syncRuns: ["runs", "sync"] as const,
  extractRuns: ["runs", "extract"] as const,
  indexRuns: ["runs", "index"] as const,
  changes: ["changes"] as const,
  runChanges: (runId: number) => ["changes", "run", runId] as const,
  pending: (courseMoodleId?: number, moduleCmid?: number, offset = 0) =>
    ["approvals", "pending", courseMoodleId ?? "all", moduleCmid ?? "all", offset] as const,
  history: (statuses: ApprovalStatus[], courseMoodleId?: number, moduleCmid?: number, reviewedBy?: number, offset = 0) =>
    ["approvals", "history", statuses.join(","), courseMoodleId ?? "all", moduleCmid ?? "all", reviewedBy ?? "anyone", offset] as const,
  approvalSummary: ["approvals", "summary"] as const,
  modules: ["modules"] as const,
  document: (id: number) => ["documents", id] as const,
  contentGaps: ["content-gaps"] as const,
  documents: ["documents", "all"] as const,
  systemStatus: ["system-status"] as const,
  // Everything under "transcriptions" is invalidated together.
  transcriptionAssets: (opts: object) => ["transcriptions", "assets", opts] as const,
  assetTranscription: (id: number) => ["transcriptions", "asset", id] as const,
  transcriptionWatcher: ["transcriptions", "watcher"] as const,
  transcriptionFailures: ["transcriptions", "failures"] as const,
  task: (taskId: string) => ["tasks", taskId] as const,
  // Everything under "sim" is invalidated together after any sim mutation.
  simScenarios: (opts: object) => ["sim", "scenarios", opts] as const,
  simScenario: (id: number) => ["sim", "scenario", id] as const,
  simScenarioReport: (id: number) => ["sim", "report", id] as const,
  simAttempts: (opts: object) => ["sim", "attempts", opts] as const,
  simAttempt: (id: number) => ["sim", "attempt", id] as const,
  simSummary: (opts: object) => ["sim", "summary", opts] as const,
};

export const POLL_MS = 3000;
// /admin/status runs live checks with a 5s limit each; poll gently.
export const STATUS_POLL_MS = 45_000;

// Jobs this browser starts are tracked through /tasks/{id} (see JobWatcher).
// A run list only polls itself if a row reports "running", e.g. a scheduled
// job, should the API ever commit runs before they finish.
export function shouldPoll(runs: AnyRun[] | undefined) {
  return !!runs?.some((run) => run.status === "running");
}

const runsRefetch = (runs: AnyRun[] | undefined) => (shouldPoll(runs) ? POLL_MS : false);

// --- reads -------------------------------------------------------------------

// First page (up to 200) of live courses, for name lookups and pickers.
export function useCourses() {
  return useQuery({ queryKey: keys.courses, queryFn: () => moodle.listCourses() });
}

export function useCoursePage(offset: number, limit: number, includeDeleted = false) {
  return useQuery({
    queryKey: keys.coursePage(offset, limit, includeDeleted),
    queryFn: () => moodle.listCourses({ offset, limit, includeDeleted }),
    placeholderData: keepPreviousData,
  });
}

export function useCourse(moodleId: number, enabled = true) {
  return useQuery({ queryKey: keys.course(moodleId), queryFn: () => moodle.getCourse(moodleId), enabled });
}

export function useSyncRuns() {
  return useQuery({
    queryKey: keys.syncRuns,
    queryFn: () => moodle.listSyncRuns(),
    refetchInterval: (q) => runsRefetch(q.state.data),
  });
}

export function useExtractionRuns() {
  return useQuery({
    queryKey: keys.extractRuns,
    queryFn: () => moodle.listExtractionRuns(),
    refetchInterval: (q) => runsRefetch(q.state.data?.runs),
  });
}

export function useIndexRuns() {
  return useQuery({
    queryKey: keys.indexRuns,
    queryFn: () => rag.listIndexRuns(),
    refetchInterval: (q) => runsRefetch(q.state.data?.runs),
  });
}

export function useSystemStatus() {
  return useQuery({
    queryKey: keys.systemStatus,
    queryFn: admin.getSystemStatus,
    refetchInterval: STATUS_POLL_MS,
    retry: false,
  });
}

const CHANGES_PAGE = 500;
const CHANGES_MAX_PAGES = 20;

// Every change event one sync run recorded, filtered on the server.
export function useRunChanges(runId: number) {
  return useQuery({
    queryKey: keys.runChanges(runId),
    staleTime: Infinity, // a finished run's events never change
    queryFn: async () => {
      let sinceId = 0;
      const events: SyncEvent[] = [];
      for (let page = 0; page < CHANGES_MAX_PAGES; page++) {
        const res = await moodle.listChanges({ syncRunId: runId, sinceId, limit: CHANGES_PAGE });
        events.push(...res.events);
        if (res.count < CHANGES_PAGE) return { events, capped: false };
        sinceId = res.next_since_id;
      }
      return { events, capped: true };
    },
  });
}

export function usePendingApprovals(courseMoodleId: number | undefined, moduleCmid: number | undefined, offset: number, limit = 50) {
  return useQuery({
    queryKey: keys.pending(courseMoodleId, moduleCmid, offset),
    queryFn: () => moodle.listPendingApprovals({ courseMoodleId, moduleCmid, offset, limit }),
    placeholderData: keepPreviousData,
  });
}

export function useReviewHistory(opts: {
  statuses: ApprovalStatus[];
  courseMoodleId?: number;
  moduleCmid?: number;
  reviewedByUserId?: number;
  offset: number;
  limit?: number;
}) {
  const { statuses, courseMoodleId, moduleCmid, reviewedByUserId, offset, limit = 50 } = opts;
  return useQuery({
    queryKey: keys.history(statuses, courseMoodleId, moduleCmid, reviewedByUserId, offset),
    queryFn: () => moodle.listReviewedDocuments({ statuses, courseMoodleId, moduleCmid, reviewedByUserId, offset, limit }),
    placeholderData: keepPreviousData,
  });
}

export function useApprovalSummary() {
  return useQuery({ queryKey: keys.approvalSummary, queryFn: moodle.getApprovalSummary });
}

export function useModules(opts: { failed?: boolean; courseMoodleId?: number; offset?: number; limit?: number }) {
  return useQuery({
    queryKey: [...keys.modules, opts],
    queryFn: () => moodle.listModules(opts),
    placeholderData: keepPreviousData,
  });
}

export function useDocument(documentId: number | null) {
  return useQuery({
    queryKey: keys.document(documentId ?? 0),
    queryFn: () => moodle.getDocument(documentId!),
    enabled: documentId !== null,
  });
}

// Every live extracted document (metadata only). Used to show what each
// extraction run produced, since runs don't link to their documents (API gap).
export function useAllDocuments(enabled = true) {
  return useQuery({
    queryKey: keys.documents,
    enabled,
    queryFn: async () => {
      let sinceId = 0;
      const all: MoodleDocument[] = [];
      for (let page = 0; page < 100; page++) {
        const res = await moodle.listDocuments({ sinceId, limit: 200 });
        all.push(...res.documents);
        if (res.count < 200) break;
        sinceId = res.next_since_id;
      }
      return all;
    },
  });
}

// Internal course_id → mirrored course. /moodle/documents still only exposes
// the internal id; one limit=1 documents call per course recovers it.
export function useCourseIdMap() {
  const courses = useCourses();
  return useQuery({
    queryKey: ["courses", "id-map", courses.data?.courses.map((c) => c.moodle_id)],
    enabled: !!courses.data,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const map = new Map<number, MoodleCourse>();
      await Promise.all(
        (courses.data?.courses ?? []).map(async (c) => {
          const res = await moodle.listDocuments({ courseMoodleId: c.moodle_id, limit: 1 });
          const internalId = res.documents[0]?.course_id;
          if (internalId !== undefined) map.set(internalId, c);
        })
      );
      return map;
    },
  });
}

// Live documents grouped by the extraction run that wrote them (see
// lib/attribution.ts for why this is inferred).
export function useExtractionOutput(runs: ExtractionRun[] | undefined) {
  const docs = useAllDocuments(!!runs?.length);
  const byRun = useMemo(
    () => (runs && docs.data ? attributeDocuments(runs, docs.data) : undefined),
    [runs, docs.data]
  );
  return { byRun, isLoading: docs.isLoading, error: docs.error };
}

export function useContentGaps() {
  return useQuery({ queryKey: keys.contentGaps, queryFn: () => rag.getContentGaps(10) });
}

// --- pipeline triggers ---------------------------------------------------------

type QC = ReturnType<typeof useQueryClient>;

// Everything a finished pipeline job can change.
export function invalidatePipeline(qc: QC) {
  for (const key of [["runs"], keys.courses, keys.changes, ["approvals"], keys.modules, ["documents"], keys.systemStatus, ["transcriptions"]]) {
    qc.invalidateQueries({ queryKey: key });
  }
}

type RunLike = { id: number; status: string; error?: string | null };

function isQueued(res: unknown): res is { mode: "queued"; task_id: string } {
  return !!res && typeof res === "object" && (res as { mode?: string }).mode === "queued";
}

// Registers a client-side job per kind the call kicks off. A queued response
// hands its task id to the job (JobWatcher polls it); an inline response has
// already finished, so its run (found via `runOf`) is recorded right away.
function usePipelineMutation<A, R>(
  fn: (args: A) => Promise<R>,
  jobs: { kind: JobKind; label: string }[],
  runOf: (res: R, kind: JobKind) => RunLike | undefined = (res) =>
    (res as { run?: RunLike } | undefined)?.run
) {
  const qc = useQueryClient();
  const kinds = jobs.map((j) => j.kind);
  return useMutation({
    mutationFn: fn,
    onMutate: () => {
      for (const job of jobs) startJob(job.kind, job.label);
    },
    onSuccess: (res) => {
      if (isQueued(res)) {
        for (const kind of kinds) attachTask(kind, res.task_id);
        return;
      }
      for (const kind of kinds) {
        const run = runOf(res, kind);
        finishJobs([kind], {
          taskId: null,
          status: run?.status ?? "success",
          runId: run?.id ?? null,
          result: null,
          error: run?.error ?? null,
        });
      }
      invalidatePipeline(qc);
    },
    onError: () => {
      for (const kind of kinds) cancelJob(kind);
    },
  });
}

// No ids = full sync (may retire removed courses); ids = only those courses.
export function useSyncAll() {
  return usePipelineMutation(
    (courseMoodleIds?: number[]) => moodle.syncAll(courseMoodleIds),
    [{ kind: "sync", label: "Sync from Moodle" }]
  );
}

// Dry run; a mutation (not a cached query) because it's a slow POST the user
// asks for explicitly.
export function useSyncPreview() {
  return useMutation({ mutationFn: (courseMoodleIds?: number[]) => moodle.previewSync(courseMoodleIds) });
}

export function useExtractAll() {
  return usePipelineMutation(() => moodle.extractAll(), [{ kind: "extract", label: "Extract pending modules" }]);
}

export function useIndexPending() {
  return usePipelineMutation(() => rag.indexPending(), [{ kind: "index", label: "Index approved documents" }]);
}

export function useRebuildIndex() {
  return usePipelineMutation(() => rag.rebuildIndex(), [{ kind: "index", label: "Rebuild all indices" }]);
}

// Always inline: the response is the refreshed course, with no run.
export function useSyncCourse() {
  const qc = useQueryClient();
  return usePipelineMutation(
    async (moodleId: number) => {
      const course = await moodle.syncCourse(moodleId);
      qc.setQueryData(keys.course(course.moodle_id), course);
      return course;
    },
    [{ kind: "sync", label: "Sync one course" }],
    () => undefined
  );
}

export function useIndexCourse() {
  return usePipelineMutation(
    (moodleId: number) => rag.indexCourse(moodleId),
    [
      { kind: "sync", label: "Course pipeline: sync" },
      { kind: "extract", label: "Course pipeline: extract" },
      { kind: "index", label: "Course pipeline: index" },
    ],
    (res, kind) => (res.mode === "inline" ? res.result[kind as "sync" | "extract" | "index"] : undefined)
  );
}

export function useRetryFailedExtractions() {
  return usePipelineMutation(
    (scope: { cmids?: number[]; courseMoodleIds?: number[] }) => moodle.retryFailedExtractions(scope),
    [{ kind: "extract", label: "Retry failed extractions" }]
  );
}

// Always inline: the response is the extraction run itself.
export function useExtractModule() {
  return usePipelineMutation(
    (cmid: number) => moodle.extractModule(cmid),
    [{ kind: "extract", label: "Re-extract one module" }],
    (run) => run
  );
}

// --- media transcription -----------------------------------------------------------

const WATCHER_POLL_MS = 30_000;

export function useTranscriptionWatcher() {
  return useQuery({
    queryKey: keys.transcriptionWatcher,
    queryFn: moodle.getTranscriptionWatcher,
    refetchInterval: (q) => (q.state.data?.counts.processing ? POLL_MS : WATCHER_POLL_MS),
  });
}

// Polls while anything is mid-transcription, or while this browser's
// transcribe job runs, so rows move between states without a reload.
export function useAssets(opts: { status?: TranscriptionStatus; offset?: number; limit?: number }) {
  const job = useJob("transcribe");
  const processing = useTranscriptionWatcher().data?.counts.processing ?? 0;
  return useQuery({
    queryKey: keys.transcriptionAssets(opts),
    queryFn: () => moodle.listAssets(opts),
    placeholderData: keepPreviousData,
    refetchInterval: job || processing > 0 ? POLL_MS : false,
  });
}

export function useTranscriptionFailures(enabled = true) {
  const job = useJob("transcribe");
  return useQuery({
    queryKey: keys.transcriptionFailures,
    queryFn: moodle.getTranscriptionFailures,
    enabled,
    refetchInterval: job ? POLL_MS : false,
  });
}

export function useAssetTranscription(assetId: number | null) {
  return useQuery({
    queryKey: keys.assetTranscription(assetId ?? 0),
    queryFn: () => moodle.getAssetTranscription(assetId!),
    enabled: assetId !== null,
    // 404 = no transcription yet; retrying won't change that.
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 1,
  });
}

// Chosen files ({assetIds}) or every pending/failed one ({only}). Queued, so
// the transcribe job tracks the Celery task like the other pipeline triggers.
export function useTranscribe() {
  return usePipelineMutation(
    (scope: { assetIds?: number[]; only?: "pending" | "failed" }) => moodle.triggerTranscriptions(scope),
    [{ kind: "transcribe", label: "Transcription" }],
    () => undefined
  );
}

export function useToggleWatcher() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (paused: boolean) => moodle.setTranscriptionWatcher(paused),
    onSuccess: (state) => qc.setQueryData(keys.transcriptionWatcher, state),
    onSettled: () => qc.invalidateQueries({ queryKey: keys.transcriptionWatcher }),
  });
}

// --- approval -------------------------------------------------------------------

function useApprovalMutation<A, R>(fn: (args: A) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    // Also on error: a 409 (already pending/retired) means our list is stale.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["approvals"] });
      qc.invalidateQueries({ queryKey: keys.courses }); // per-course stats
      qc.invalidateQueries({ queryKey: keys.indexRuns }); // pending counts live here
    },
  });
}

export function useApprove() {
  return useApprovalMutation(({ id, note }: { id: number; note?: string }) => moodle.approveDocument(id, note));
}

export function useReject() {
  return useApprovalMutation(({ id, note }: { id: number; note?: string }) => moodle.rejectDocument(id, note));
}

export function useReopen() {
  return useApprovalMutation(({ id, note }: { id: number; note?: string }) => moodle.reopenDocument(id, note));
}

export function useBulkApprove() {
  return useApprovalMutation(moodle.bulkApprove);
}

export function useBulkReject() {
  return useApprovalMutation(moodle.bulkReject);
}

// --- simulated patients ----------------------------------------------------------

export function useScenarios(opts: { status?: ScenarioStatus; courseMoodleId?: number; q?: string; offset: number; limit: number }) {
  return useQuery({
    queryKey: keys.simScenarios(opts),
    queryFn: () => sim.listScenarios(opts),
    placeholderData: keepPreviousData,
  });
}

export function useScenario(id: number | undefined) {
  return useQuery({
    queryKey: keys.simScenario(id ?? 0),
    queryFn: () => sim.getScenario(id!),
    enabled: id !== undefined,
  });
}

export function useScenarioReport(id: number) {
  return useQuery({ queryKey: keys.simScenarioReport(id), queryFn: () => sim.getScenarioReport(id) });
}

export function useAttempts(opts: sim.AttemptFilters & { offset: number; limit: number }) {
  return useQuery({
    queryKey: keys.simAttempts(opts),
    queryFn: () => sim.listAttempts(opts),
    placeholderData: keepPreviousData,
  });
}

export function useAttempt(id: number | null) {
  return useQuery({
    queryKey: keys.simAttempt(id ?? 0),
    queryFn: () => sim.getAttempt(id!),
    enabled: id !== null,
  });
}

export function useSimSummary(opts: { courseMoodleId?: number } = {}) {
  return useQuery({ queryKey: keys.simSummary(opts), queryFn: () => sim.getSimSummary(opts) });
}

function useSimMutation<A, R>(fn: (args: A) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    // Also on error: a 409 means what we're showing is stale.
    onSettled: () => qc.invalidateQueries({ queryKey: ["sim"] }),
  });
}

export function useCreateScenario() {
  return useSimMutation(sim.createScenario);
}

export function useGenerateScenario() {
  return useSimMutation(sim.generateScenario);
}

export function useUpdateCase() {
  return useSimMutation(({ id, body }: { id: number; body: CaseTemplate }) => sim.updateCase(id, body));
}

export function useRegenerateCase() {
  return useSimMutation(({ id, section, instructions }: { id: number; section?: CaseSection; instructions?: string }) =>
    sim.regenerateCase(id, { section, instructions })
  );
}

export function useUpdateScenario() {
  return useSimMutation(({ id, body }: { id: number; body: ScenarioInput }) => sim.updateScenario(id, body));
}

export type ScenarioAction = "publish" | "unpublish" | "archive" | "duplicate" | "delete";

const SCENARIO_ACTIONS: Record<ScenarioAction, (id: number) => Promise<unknown>> = {
  publish: sim.publishScenario,
  unpublish: sim.unpublishScenario,
  archive: sim.archiveScenario,
  duplicate: sim.duplicateScenario,
  delete: sim.deleteScenario,
};

export function useScenarioAction() {
  return useSimMutation(({ id, action }: { id: number; action: ScenarioAction }) => SCENARIO_ACTIONS[action](id));
}

export function useAbandonAttempt() {
  return useSimMutation(sim.abandonAttempt);
}
